import React, {useEffect} from 'react';
import TrendValueString from './TrendValueString';
import i18n from 'i18n';
import {ChartFooterData, shouldHideChartTotal} from '@/views/storyboard/charts/utils/chartFooterData';

interface ChartIndicatorsProps {
    total: number;
    totalComparable: number;
    trendValue: string;
    queryDuration: string;
    chartQueryOutput: string | undefined;
    aggregationType?: string;
    chartSettingsView?: boolean;
    onPreviewFooterChange?: (footerData: ChartFooterData) => void;
}

const roundChartTotal = (value: number): number => Math.round(value * 100) / 100;

const ChartIndicators = React.memo<ChartIndicatorsProps>(({
    total,
    totalComparable,
    trendValue,
    queryDuration,
    chartQueryOutput,
    aggregationType,
    chartSettingsView = false,
    onPreviewFooterChange,
},
) => {
    const hasQueryDuration = queryDuration && queryDuration.trim() !== '';
    const roundedTotal = roundChartTotal(total);
    const roundedTotalComparable = roundChartTotal(totalComparable);
    const hideTotal = shouldHideChartTotal(chartQueryOutput, aggregationType);
    const showTrend = (chartQueryOutput === 'timeline_by_date'
        || (chartQueryOutput === 'terms_metric' && aggregationType !== 'histogram'))
        && parseFloat(trendValue) !== 0;

    useEffect(() => {
        if (!chartSettingsView) {
            return;
        }

        onPreviewFooterChange?.({
            total: roundedTotal,
            totalComparable: roundedTotalComparable,
            trendValue,
            queryDuration,
            showTrend,
            hideTotal,
        });
    }, [
        chartSettingsView,
        onPreviewFooterChange,
        roundedTotal,
        roundedTotalComparable,
        trendValue,
        queryDuration,
        showTrend,
        hideTotal,
    ]);

    if (chartSettingsView) {
        return null;
    }

    return (
        <>
            {!hideTotal ? (
                <div className="chart-total-indicator">
                    <p className="d-flex align-items-center flex-nowrap">
                        <span className='mr-1'>{i18n.t('total_txt.text')}</span>
                        <span className='ml-1'>{roundedTotal}</span>
                        {
                            showTrend ? (
                                    <TrendValueString value={trendValue} comparableValue={roundedTotalComparable} />
                                ) : null
                        }
                    </p>
                </div>
            ) : null}

            {hasQueryDuration && (
                <div className="chart-query-time-indicator">
                    <p>
                        Query:
                        {` ${queryDuration} `}
                        sec
                    </p>
                </div>
            )}
        </>
    )
});

export default ChartIndicators;